// Cookie names for the shopper's chosen storefront branch and the manager's
// currently-selected branch in the dashboard. Kept in their own module so
// importing the name doesn't pull server-only code into client components.
export const BRANCH_COOKIE = "jana_branch";

/**
 * The panel's branch selection, one cookie PER MARKET, written only by
 * `selectManageBranchAction`. The value carries the user it was chosen by, so a second
 * person signing in on the same browser starts from their own preference instead of
 * inheriting the first one's view, and one market's choice never reads in another.
 *
 * ⚠️ A selection is a request, never a grant: `resolveManageBranch` accepts only a branch
 * the member can already see, and authorisation reads the assignment, not this.
 */
export function manageBranchCookie(tenantId: string): string {
	return `jana_manage_branch_${tenantId}`;
}

/**
 * How long a temporary switch lasts: a working shift. After it the panel opens in the
 * member's preferred branch again, so a one-off look at another branch never silently
 * becomes where they start every day.
 */
export const MANAGE_BRANCH_MAX_AGE = 12 * 60 * 60;

/** `<userId>:<branchId>` or `<userId>:*` (the aggregate). */
export function encodeManageBranch(userId: string, choice: string): string {
	return `${userId}:${choice}`;
}

/** The choice in `value`, or null when it is malformed or was made by somebody else. */
export function decodeManageBranch(value: string | undefined, userId: string): string | null {
	if (!value) return null;
	const i = value.lastIndexOf(":");
	if (i <= 0 || value.slice(0, i) !== userId) return null;
	return value.slice(i + 1) || null;
}

/**
 * The same selection, carried by the merchant app, whose bearer requests hold no cookie
 * jar. ABSENT means the aggregate, which is the app's contract: it keeps its own chosen
 * branch and sends nothing for "all branches", so the panel's preferred branch does not
 * apply to it. Like the cookie, a request and never a grant.
 */
export const MANAGE_BRANCH_HEADER = "x-jana-manage-branch";
